import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button";
import { apiClient } from "zodios/client";
import { sleep } from "../../../hooks/api";

interface GetReportButtonProps {
  queryDetails: {
    id: number;
    query_type: string;
    parameters?: Record<string, number>;
  };
  marketPlace: {
    marketPlace: string;
  };
}

type StartReportPayload = {
  query_type: string;
  subscription_id: number;
  parameters?: Record<string, number>;
  marketplace: string;
};

const GetReportButton = ({ queryDetails, marketPlace }: GetReportButtonProps) => {
  const queryClient = useQueryClient();

  const startReportMutation = useMutation({
    mutationFn: async (data: StartReportPayload) => {
      await apiClient.startReport({
        query_type: data.query_type,
        subscription_id: data.subscription_id,
        parameters: data.parameters || {},
        marketplace: data.marketplace,
      });
    },
  });

  const reportStatusQuery = useQuery({
    queryKey: ["getReportStatus", queryDetails.id, marketPlace.marketPlace],
    queryFn: async () => {
      await sleep(15000);
      while (true) {
        const res = await apiClient.getReportStatus({
          queries: {
            subscription_id: queryDetails.id,
            marketplace: marketPlace.marketPlace,
          },
        });

        switch (res.status) {
          case "SUCCEEDED":
            startReportMutation.reset();
            void queryClient.invalidateQueries({ queryKey: ["amc_reports"] });
            return res;
          case "FAILED":
            throw new Error("Report generation failed.");
          case "PENDING":
          case "RUNNING":
          default:
            await sleep(30000);
        }
      }
    },
    enabled: startReportMutation.isSuccess,
  });

  const handleGenerateReport = () => {
    startReportMutation.mutate({
      query_type: queryDetails.query_type,
      subscription_id: queryDetails.id,
      parameters: queryDetails.parameters || {},
      marketplace: marketPlace.marketPlace,
    });
  };

  const isReportPending =
    startReportMutation.isPending || (reportStatusQuery.isFetching && reportStatusQuery.data?.status !== "SUCCEEDED");

  return (
    <Button onPress={handleGenerateReport} isPending={isReportPending}>
      {isReportPending ? "Generating..." : "Generate Report"}
    </Button>
  );
};

export default GetReportButton;
